import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { AppContext } from '../../App';
import { getIdFromUrl } from '../../utils/getIdFromUrl';
import useGlynt from './useGlynt';

export default function useSetOrg(dpid?: string) {
  const {
    dataPoolId,
    setDataPoolId,
    dataPoolLabel,
    setDataPoolLabel,
    org,
    orgId,
    setOrg,
    setOrgId,
    orgLabel,
    setOrgLabel,
  } = React.useContext(AppContext);

  const glynt = useGlynt(dpid);

  const { data, isPending, isError } = useQuery({
    queryKey: ['dataPool', dpid],
    queryFn: async () => (await glynt).dataPool.retrieve(),
    enabled: !!dpid,
    refetchOnWindowFocus: false,
  });

  React.useEffect(() => {
    if (data) {
      if (!dataPoolId || dataPoolId !== data.id) {
        setDataPoolId(data.id);
      }

      if (!dataPoolLabel || dataPoolLabel !== data.label) {
        setDataPoolLabel(data.label);
      }

      const dpOrgId = getIdFromUrl(data.organization);
      if (!orgId || orgId !== dpOrgId) {
        setOrgId(dpOrgId);
      }
    }
  }, [data, dataPoolId, dataPoolLabel, orgId, setDataPoolId, setDataPoolLabel, setOrgId]);

  let dpOrgId = data ? getIdFromUrl(data.organization) : undefined;

  const { data: orgData } = useQuery({
    queryKey: ['org', dpOrgId],
    queryFn: async () => (await glynt).org.retrieve(dpOrgId),
    enabled: !!dpOrgId,
    refetchOnWindowFocus: false,
  });

  React.useEffect(() => {
    if (orgData) {
      if (!orgLabel || orgLabel !== orgData.label) {
        setOrgLabel(orgData.label);
      }

      if (!org || org !== orgData) {
        setOrg(orgData);
      }
    }
  }, [orgData, orgLabel, org, setOrgLabel, setOrg]);

  // Keep loading until the context dataPoolId has synced to the fetched pool.
  // Otherwise consumers gated on `loading` mount their children for one render
  // with the previous pool's dataPoolId still in context, firing a request
  // against the old data pool before the sync effect commits.
  const isSyncing = !!data && dataPoolId !== data.id;

  return { loading: isPending || isSyncing, error: isError };
}
